import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { configApi } from '../../lib/api';
import { getCachedIdeas, setCachedIdeas } from '../../lib/pickerIdeasCache';
import { selectionId } from './ideaCore';
import type { IdeaBatch, IdeaLength, Selection } from './types';

/**
 * Fetching one set of ideas: session cache first (keyed by language, selection,
 * hero name, context and length), then the ideas endpoint, then the localized
 * fallback classics. A request sequence guard drops replies that a newer
 * request has overtaken. `load` resolves with the batch, or null when it lost.
 */
export default function useIdeaCards(language: string, heroName: string, themeHint = '', childHero = false, surface = '') {
  const { t } = useTranslation();
  const [batch, setBatch] = useState<IdeaBatch | null>(null);
  const [loading, setLoading] = useState(false);
  const requestSeq = useRef(0);

  const load = useCallback(async (sel: Selection, length: IdeaLength, ctx: string, force = false): Promise<IdeaBatch | null> => {
    const seq = ++requestSeq.current;
    const id = selectionId(sel);
    const label = sel ? (sel.kind === 'key' ? t(`interests.items.${sel.key}`) : sel.label.trim()) : null;
    const keyTag = sel?.kind === 'key' ? sel.key : null;
    const cacheKey = `${id}|${heroName || '-'}|${ctx || '-'}|${themeHint ? themeHint.slice(0, 80) : '-'}|${childHero ? 'child' : '-'}`;
    if (!force) {
      const cached = getCachedIdeas(language, cacheKey, length);
      if (cached) {
        const next: IdeaBatch = { id, keyTag, label, length, ideas: cached, fallback: false };
        setBatch(next);
        return next;
      }
    }
    setLoading(true);
    try {
      const res = await configApi.getIdeas(language, length, label ? [label] : [], ctx, heroName, themeHint, childHero, surface);
      if (seq !== requestSeq.current) return null; // a newer request won
      const ideas = res.data.ideas.filter((i) => i.trim());
      if (ideas.length > 0) {
        setCachedIdeas(language, cacheKey, length, ideas);
        const next: IdeaBatch = { id, keyTag, label, length, ideas, fallback: false };
        setBatch(next);
        return next;
      }
      throw new Error('no ideas');
    } catch {
      if (seq !== requestSeq.current) return null;
      const next: IdeaBatch = {
        id, keyTag, label, length, fallback: true,
        ideas: [t('create.ideaSet2a'), t('create.ideaSet2b'), t('create.ideaSet2c')],
      };
      setBatch(next);
      return next;
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [language, heroName, themeHint, childHero, t]);

  return { batch, loading, load };
}
